import { Link } from "react-router-dom";
/**
 * The read-mostly screens: a table, a document, a list. Grouped because
 * they share a shape and separating them into files of twenty lines each
 * would be filing rather than structure.
 */
import { label } from "../components/Money";
import { PackDocument } from "../components/PackDocument";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";
import { Scrolls } from "../components/Table";

export function Programme() {
  const { data, error, loading } = useApi<{
    versions: any[]; ledger: any[];
  }>("/programme");
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  return (
    <>
      <h1>Programme register</h1>
      <p className="standfirst">
        Which programme is the Accepted Programme, on what basis, and since when.
        Acceptance is read from the ledger, never from a status column.
      </p>
      <Scrolls>
        <table>
          <thead><tr><th>Revision</th><th>Submitted</th><th>Status</th><th className="num">Planned Completion</th></tr></thead>
          <tbody>
            {data.versions.map((v) => (
              <tr key={v.id}>
                <td className="mn">{v.revision}</td>
                <td className="mn">{v.submitted_date ?? "—"}</td>
                {/* A submitted revision is waiting on the Project Manager;
                    accepted and superseded are settled. */}
                <td><span className={`pill${v.status === "submitted" ? " act"
                  : v.status === "superseded" ? " done" : ""}`}>{label(v.status)}</span></td>
                <td className="num mn">{v.planned_completion ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Scrolls>
      <h2 className="ser" style={{ fontSize: 20, fontWeight: 500, marginTop: 28 }}>
        Acceptance ledger <span className="withheld" style={{ fontSize: 13 }}>append-only</span>
      </h2>
      <Scrolls>
        <table>
          <thead><tr><th>Effective</th><th>Revision</th><th>Transition</th><th>Basis</th></tr></thead>
          <tbody>
            {data.ledger.map((e, i) => (
              <tr key={i}>
                <td className="mn">{e.effective_date}</td>
                <td className="mn">{e.revision ?? "—"}</td>
                <td>{label(e.transition)}</td>
                <td className="mn withheld">{e.basis} · cl.{e.clause}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Scrolls>
    </>
  );
}

export function Pack() {
  const { data, error, loading } = useApi<{
    as_of: string; markdown: string; redacted: boolean;
  }>("/pack");
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  return (
    <>
      <h1>Weekly commercial pack</h1>
      <p className="standfirst">
        Week ending {data.as_of}. What the commercial meeting needs to decide,
        assembled from the same clocks the radar runs on.
      </p>
      {data.redacted && (
        <div className="note amber">
          <span className="h">Figures withheld</span>
          Your role does not include financial visibility, so currency amounts
          read <code>[withheld]</code>.
        </div>
      )}
      <section className="card"><PackDocument markdown={data.markdown} /></section>
    </>
  );
}

export function Audit() {
  const { data, error, loading } = useApi<{ records: any[] }>("/audit");
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  return (
    <>
      <h1>Audit journal</h1>
      <p className="standfirst">
        Every change to the canonical store, in order. Append-only — this page
        cannot alter it, and neither can anybody else.
      </p>
      <p className="hint">
        Decisions by people. Ingest, evaluation and drafting are hidden: they are
        the great majority of the rows and none of them is a decision.
      </p>
      <Scrolls>
        <table>
          <thead><tr><th>Recorded</th><th>Actor</th><th>Change</th><th>Entity</th><th>Detail</th></tr></thead>
          <tbody>
            {data.records.map((r, i) => (
              <tr key={i}>
                <td className="mn">{r.recorded_at}</td>
                <td>{r.actor}</td>
                {/* What kind of change a journal row records is a
                    classification. Nothing here is waiting for anybody. */}
                <td><span className="pill">{label(r.action)}</span></td>
                <td>{label(r.entity_type)} <span className="mn withheld">{String(r.entity_id).slice(0, 12)}</span></td>
                <td className="mn" style={{ fontSize: 11 }}>{describeDetail(r.detail)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Scrolls>
      {data.records.length === 0 && <p className="empty">Nothing yet.</p>}
    </>
  );
}

/**
 * What a journal row changed, in words. `{status: {from, to}}` reads as
 * "status: drafted → sent". A hash reads as a hash. Anything else is shown
 * as key: value rather than hidden -- the column used to render a dash for
 * every row but a ruleset confirmation, so the auditor could see that
 * something changed five times in a minute and not what to.
 */
export function describeDetail(detail: Record<string, any> | null | undefined): string {
  if (!detail || Object.keys(detail).length === 0) return "—";
  return Object.entries(detail).map(([key, value]) => {
    if (key === "content_hash") return `hash ${value}`;
    if (value && typeof value === "object" && "from" in value && "to" in value) {
      return `${key}: ${String(value.from ?? "—")} → ${String(value.to ?? "—")}`;
    }
    if (value && typeof value === "object") return `${key}: ${JSON.stringify(value)}`;
    return `${key}: ${String(value)}`;
  }).join(" · ");
}

/** How letters are drafted, in the reader's words rather than the code's. */
export function describeDrafting(provider: string | null): string {
  if (!provider || provider === "mock") {
    return "deterministic templates — no hosted model configured";
  }
  const hosted: Record<string, string> = {
    azure_openai: "GPT in your Azure OpenAI resource",
    azure_foundry: "Claude in your Azure AI Foundry resource",
    anthropic: "Claude, called directly",
  };
  const named = hosted[provider];
  // Every hosted path keeps the deterministic letter as its floor, so an
  // outage cannot stop a deadline being chased.
  return named ? `${named} — templates as the fallback` : provider;
}

export function Settings() {
  const { data, error, loading } = useApi<any>("/settings");
  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  return (
    <>
      <h1>Settings</h1>
      <p className="standfirst">Where each source is read from, and how the engine is configured.</p>
      {/* This said changing configuration "requires the contract authority
          role", shown only to people without it — which implied that role
          could change it here. Nobody can: these values are held in a file
          on the host. It is shown to everybody now, because it is true for
          everybody, and it points at the one thing the application can
          change. */}
      <div className="note">
        <span className="h">Held on the host</span>
        These values come from a configuration file on the server and are not
        editable from here by anybody. What <em>can</em> be changed from the
        application is what the engine reads —{" "}
        <Link to="/sources">the source folders</Link>.
      </div>
      <section className="card">
        <h2 className="secttl">Adapters</h2>
        <Scrolls>
          <table>
            <thead><tr><th>Source</th><th>Reading from</th><th>Location</th></tr></thead>
            <tbody>
              {data.adapters.map((a: any) => (
                <tr key={a.name}>
                  <td>{a.name}</td>
                  {/* A local folder is the pilot's stand-in for the real
                      system, so it is the temporary state worth marking --
                      green had it exactly backwards. */}
                  <td><span className={`pill${a.source === "local" ? " act" : ""}`}>{a.source}</span></td>
                  <td className="mn withheld">{a.path ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Scrolls>
      </section>
      <section className="card">
        <h2 className="secttl">Engine</h2>
        <Scrolls>
          <table>
            <tbody>
              <tr><td>Evaluated as at</td><td className="mn">{data.engine.as_of ?? "today, at each poll"}</td></tr>
              <tr><td>Contract timezone</td><td className="mn">{data.engine.timezone}</td></tr>
              {/* "mock" is what the code calls it. What is true is that
                  drafting runs on deterministic templates because no hosted
                  model is configured — a defensible position that sounds
                  like one when it is said in words. */}
              <tr><td>Drafting</td><td>{describeDrafting(data.llm)}</td></tr>
              <tr><td>Human confirmation</td><td><span className="pill on">required — cannot be disabled</span></td></tr>
              <tr><td>Store backend</td><td className="mn">{data.store}</td></tr>
              <tr><td>Identity provider</td><td className="mn">{data.identity}</td></tr>
            </tbody>
          </table>
        </Scrolls>
      </section>
    </>
  );
}
